import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { declarationsApi } from '../api/declarations.api';
import { PeriodeSelector } from '../../bulletins/components/PeriodeSelector';

const now = new Date();

type StatutDeclaration = 'BROUILLON' | 'VALIDE' | 'TRANSMIS';

interface DeclarationData {
  idDeclaration: string;
  type: 'CNSS' | 'ITS';
  mois: number;
  annee: number;
  statut: StatutDeclaration;
  montantTotal: number;
  nombreEmployes: number;
  lignes: Array<{ nomEmploye: string; prenomEmploye: string; montant: number }>;
  dateGeneration: string;
  dateValidation: string | null;
  dateTransmission: string | null;
}

const STATUT_CONFIG: Record<StatutDeclaration, { label: string; className: string }> = {
  BROUILLON: { label: 'Brouillon', className: 'bg-warning-light text-warning' },
  VALIDE:    { label: 'Validé',    className: 'bg-accent-light text-accent' },
  TRANSMIS:  { label: 'Transmis',  className: 'bg-success-light text-success' },
};

function StatutBadge({ statut }: { statut: StatutDeclaration }) {
  const cfg = STATUT_CONFIG[statut] ?? STATUT_CONFIG.BROUILLON;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function formatMois(mois: number, annee: number) {
  return new Date(annee, mois - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

export function DeclarationsListePage() {
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const queryClient = useQueryClient();

  const cnss = useQuery<DeclarationData>({
    queryKey: ['declaration-cnss', { mois, annee }],
    queryFn: () => declarationsApi.cnss(annee, mois),
  });
  const its = useQuery<DeclarationData>({
    queryKey: ['declaration-its', { mois, annee }],
    queryFn: () => declarationsApi.its(annee, mois),
  });
  const historique = useQuery<DeclarationData[]>({
    queryKey: ['declarations-historique', { annee }],
    queryFn: () => declarationsApi.lister({ annee }),
  });

  const invaliderCache = () => {
    queryClient.invalidateQueries({ queryKey: ['declaration-cnss', { mois, annee }] });
    queryClient.invalidateQueries({ queryKey: ['declaration-its', { mois, annee }] });
    queryClient.invalidateQueries({ queryKey: ['declarations-historique', { annee }] });
  };

  const validerCNSS = useMutation({
    mutationFn: () => declarationsApi.validerCNSS(annee, mois),
    onSuccess: invaliderCache,
  });
  const validerITS = useMutation({
    mutationFn: () => declarationsApi.validerITS(annee, mois),
    onSuccess: invaliderCache,
  });
  const marquerTransmis = useMutation({
    mutationFn: (idDeclaration: string) => declarationsApi.marquerTransmis(idDeclaration),
    onSuccess: invaliderCache,
  });

  const f = (n: number) => n?.toLocaleString('fr-FR') ?? '—';

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-content">Déclarations</h1>
        <PeriodeSelector mois={mois} annee={annee} onChange={(m, a) => { setMois(m); setAnnee(a); }} />
      </div>

      {/* Cartes CNSS / ITS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DeclarationCard
          titre="Déclaration CNSS"
          query={cnss}
          f={f}
          onValider={() => validerCNSS.mutate()}
          onTransmettre={(id) => marquerTransmis.mutate(id)}
          isPendingValider={validerCNSS.isPending}
          isPendingTransmettre={marquerTransmis.isPending}
        />
        <DeclarationCard
          titre="Déclaration ITS"
          query={its}
          f={f}
          onValider={() => validerITS.mutate()}
          onTransmettre={(id) => marquerTransmis.mutate(id)}
          isPendingValider={validerITS.isPending}
          isPendingTransmettre={marquerTransmis.isPending}
        />
      </div>

      {/* Historique */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h2 className="font-semibold text-content mb-4">Historique des déclarations — {annee}</h2>
        {historique.isLoading && <p className="text-sm text-content-subtle">Chargement...</p>}
        {historique.data && historique.data.length === 0 && (
          <p className="text-sm text-content-subtle">Aucune déclaration enregistrée pour {annee}.</p>
        )}
        {historique.data && historique.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-subtle border-b border-border">
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Période</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 pr-4 text-right">Montant total</th>
                  <th className="py-2">Date génération</th>
                </tr>
              </thead>
              <tbody>
                {historique.data.map((d) => (
                  <tr key={d.idDeclaration} className="border-t border-border hover:bg-surface-raised">
                    <td className="py-2 pr-4 font-medium">{d.type}</td>
                    <td className="py-2 pr-4 capitalize">{formatMois(d.mois, d.annee)}</td>
                    <td className="py-2 pr-4"><StatutBadge statut={d.statut} /></td>
                    <td className="py-2 pr-4 text-right font-medium">{f(d.montantTotal)} F</td>
                    <td className="py-2 text-content-subtle text-xs">
                      {new Date(d.dateGeneration).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface DeclarationCardProps {
  titre: string;
  query: ReturnType<typeof useQuery<DeclarationData>>;
  f: (n: number) => string;
  onValider: () => void;
  onTransmettre: (id: string) => void;
  isPendingValider: boolean;
  isPendingTransmettre: boolean;
}

function DeclarationCard({ titre, query, f, onValider, onTransmettre, isPendingValider, isPendingTransmettre }: DeclarationCardProps) {
  const { data, isLoading, isError } = query;
  return (
    <div className="bg-surface border border-border rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-content">{titre}</h2>
        {data && <StatutBadge statut={data.statut} />}
      </div>

      {isLoading && <p className="text-content-subtle text-sm">Chargement...</p>}
      {isError && <p className="text-amber-600 text-sm">Aucun bulletin pour cette période.</p>}

      {data && (
        <>
          <div>
            <p className="text-3xl font-bold text-content">
              {f(data.montantTotal)} <span className="text-base font-normal text-content-subtle">FCFA</span>
            </p>
            <p className="text-sm text-content-muted mt-1">{data.nombreEmployes} employé(s)</p>
          </div>

          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-content-subtle">
                  <th className="text-left py-1">Employé</th>
                  <th className="text-right py-1">Montant</th>
                </tr>
              </thead>
              <tbody>
                {data.lignes?.map((l, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1">{l.nomEmploye} {l.prenomEmploye}</td>
                    <td className="text-right py-1 font-medium">{f(l.montant)} F</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Actions selon statut */}
          <div className="flex gap-2 pt-1">
            {data.statut === 'BROUILLON' && (
              <button
                onClick={onValider}
                disabled={isPendingValider}
                className="flex-1 py-2 px-4 rounded-md bg-primary text-content-inverted text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
              >
                {isPendingValider ? 'Validation...' : 'Valider'}
              </button>
            )}
            {data.statut === 'VALIDE' && (
              <button
                onClick={() => onTransmettre(data.idDeclaration)}
                disabled={isPendingTransmettre}
                className="flex-1 py-2 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isPendingTransmettre ? 'Transmission...' : 'Marquer transmis'}
              </button>
            )}
            {data.statut === 'TRANSMIS' && (
              <p className="text-xs text-success font-medium pt-1">
                Transmis le {data.dateTransmission ? new Date(data.dateTransmission).toLocaleDateString('fr-FR') : '—'}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
